import { Type } from '@sinclair/typebox';
// ─── ViewManifest (IV1-S8) ──────────────────────────────────────────────────
// The always-present source-of-truth contract written alongside every
// generated view's `view.sysml`/`view.svg`. `NodeAutoViewWriter` stages a
// `manifest.json` inside the same atomically-renamed view directory; the
// `GET /views` list handler reads it best-effort and surfaces it on each
// `ViewSummary`. The raw SysML v2 the view was rendered from is the model's
// ground truth — `sysmlSourcePath` makes it a first-class, machine-resolvable
// field the SPA can always trace back to. Declared ABOVE `ViewSummarySchema`
// so the `Type.Optional(ViewManifestSchema)` reference resolves at module-eval
// time (both are plain JS const objects; ordering matters).
export const ViewManifestSchema = Type.Object({
    /**
     * The visualization engine that produced this view. `deterministic` is
     * the only engine wired today (the synthesiser + SVG renderer); the
     * `llm-image` / `diagram-as-code` literals reserve the contract for the
     * later visualization epics so legacy manifests stay forward-compatible.
     */
    engine: Type.Union([
        Type.Literal('deterministic'),
        Type.Literal('llm-image'),
        Type.Literal('diagram-as-code'),
    ]),
    /**
     * Workspace-relative posix path to the view's `view.sysml` — the raw
     * SysML v2 source of truth the view was rendered from. Same shape as
     * `ViewSummary.sourcePath` (`<viewsOutputPath>/<viewId>/view.sysml`).
     */
    sysmlSourcePath: Type.String(),
    /** Hash of the SceneDescription the view was rendered from, when known. */
    sceneContentHash: Type.Optional(Type.String()),
    /**
     * True when the rendered artefact is illustrative only (e.g. an LLM
     * raster) and not a structurally-faithful projection of the model.
     */
    illustrative: Type.Optional(Type.Boolean()),
    /**
     * Set to `'unverifiable'` when connectivity in the rendered artefact
     * cannot be machine-verified against the model (e.g. raster output).
     */
    connectivityVerdict: Type.Optional(Type.Literal('unverifiable')),
    /**
     * LV2-S2 — the sticky per-view LEAD representation: which engine the SPA
     * surfaces first for this view. `'sketch'` is the deterministic React Flow
     * default; `'llm-image'` leads with the raster (still keeping the sketch +
     * SysML source one action away).
     *
     * OPTIONAL + UNDEFAULTED on purpose: a legacy manifest and the default
     * deterministic write BOTH omit it, and the consumer treats absent as
     * `'sketch'`. The writer MUST NOT stamp `'sketch'` on the deterministic
     * path — emitting the literal would change every auto-write's `manifest.json`
     * bytes and break the byte-identical switch-OFF invariant. Only the raster /
     * escalation path (and the manual lead-engine switch route) stamps the
     * field. `additionalProperties` is unset here (default permissive), so a
     * persisted `leadEngine` round-trips through `Value.Check` unchanged.
     */
    leadEngine: Type.Optional(Type.Union([Type.Literal('sketch'), Type.Literal('llm-image')])),
}, { $id: 'ViewManifest' });
// ─── LV2-S2 lead-engine switch contract (PUT /views/:viewId/lead-engine) ────
// The manual engine-switch seam: a representation choice (NOT geometry) so it
// belongs with the manifest, not the layout PUT. The SPA's LV3-S2 switcher hits
// this route to flip + persist the sticky per-view lead engine.
/** The sticky per-view lead representation. Absent on a manifest ⇒ `'sketch'`. */
export const LeadEngineSchema = Type.Union([Type.Literal('sketch'), Type.Literal('llm-image')], { $id: 'LeadEngine' });
/** Request body for `PUT /views/:viewId/lead-engine`. */
export const ViewLeadEnginePutBodySchema = Type.Object({ leadEngine: LeadEngineSchema }, { additionalProperties: false, $id: 'ViewLeadEnginePutBody' });
/** Response for `PUT /views/:viewId/lead-engine` — echoes the persisted choice. */
export const ViewLeadEnginePutResponseSchema = Type.Object({ leadEngine: LeadEngineSchema }, { $id: 'ViewLeadEnginePutResponse' });
// ─── In-view relayout contract (POST /views/:viewId/relayout) ───────────────
// The stored-view editor's prominent "Adjust layout" box hits this route to
// re-lay-out an EXISTING view in place from a natural-language request
// ("move the BMS up", "spread it out"). It wraps the SAME `relayoutView()`
// server module the chat "adjust layout" loop uses (LV1-S6), but exposed as a
// standalone REST call so the Views tab does not have to round-trip through a
// chat message. The compiled `LayoutIntent` is persisted into the view's
// `layout.json`; the SPA re-fetches the layout and re-applies its deterministic
// ELK + post-pass. An aesthetic/free-form request the directive DSL can't
// express returns `applied:false` with the `expressiveness-gap` escalation —
// the UI then nudges the user toward the LLM visualization.
/** Request body for `POST /views/:viewId/relayout`. */
export const ViewRelayoutBodySchema = Type.Object({
    /** The user's plain-language layout request (non-empty). */
    request: Type.String({ minLength: 1 }),
}, { additionalProperties: false, $id: 'ViewRelayoutBody' });
/**
 * Response for `POST /views/:viewId/relayout`. `applied:true` means the compiled
 * intent was accepted; `changed:true` means it was persisted + the view
 * re-emitted (the SPA should re-fetch the layout), while `changed:false` means
 * the view already renders exactly this arrangement (nothing was written — the
 * UI should say so instead of claiming an update). `applied:false` +
 * `escalation:'expressiveness-gap'` means the request is inexpressible by the
 * deterministic DSL — the caller offers the LLM raster.
 */
export const ViewRelayoutResponseSchema = Type.Object({
    applied: Type.Boolean(),
    /** Present on `applied:true` — whether anything was actually re-written. */
    changed: Type.Optional(Type.Boolean()),
    escalation: Type.Optional(Type.Literal('expressiveness-gap')),
}, { $id: 'ViewRelayoutResponse' });
// ─── Diagram agent — POST /views/:viewId/edit ────────────────────────────────
// The unified natural-language edit endpoint for a STORED view (the "diagram
// agent"). One request, three deterministic engines behind one seam: content
// (add/remove KB elements shown in this view), scope (hide/collapse), and
// arrangement (the LV1 layout DSL). The agent is view-scoped by contract: it
// NEVER mutates the knowledge base — a model-change or general-question request
// comes back as `outOfScope: true` so the UI can point the user at the chat.
/** Request body for `POST /views/:viewId/edit`. */
export const ViewEditBodySchema = Type.Object({
    /** The user's plain-language edit request (non-empty). */
    request: Type.String({ minLength: 1 }),
}, { additionalProperties: false, $id: 'ViewEditBody' });
/**
 * Response for `POST /views/:viewId/edit`.
 * - `applied:true, changed:true` — an engine applied a change in place and the
 *   view was re-emitted (the SPA re-fetches). `kind` says which engine.
 * - `applied:true, changed:false` — the request compiled to a no-op (the view
 *   already looks like that), or `outOfScope:true` — the request is not about
 *   THIS diagram (a model change / general question); nothing was touched.
 * - `applied:false` + `escalation` — inexpressible by the deterministic
 *   engines; the UI may offer the LLM raster.
 */
export const ViewEditResponseSchema = Type.Object({
    applied: Type.Boolean(),
    changed: Type.Optional(Type.Boolean()),
    /** Which engine handled the request (present when applied). */
    kind: Type.Optional(Type.Union([Type.Literal('content'), Type.Literal('scope'), Type.Literal('layout')])),
    /** One-line human-readable outcome ("Added Radiator and CoolantPump."). */
    summary: Type.Optional(Type.String()),
    escalation: Type.Optional(Type.Literal('expressiveness-gap')),
    /** True when the request is not a diagram edit (model change / question). */
    outOfScope: Type.Optional(Type.Boolean()),
}, { $id: 'ViewEditResponse' });
export const ViewSummarySchema = Type.Object({
    viewId: Type.String(),
    viewName: Type.String(),
    /** ISO-8601 string of the view directory's mtime. */
    lastModified: Type.String(),
    /** SPA-facing URL for the rendered SVG thumbnail. */
    thumbnailUrl: Type.String(),
    /** Workspace-relative path to the view's `view.sysml` source. */
    sourcePath: Type.String(),
    /**
     * Source-of-truth artifact contract (IV1-S8). Present when the view dir
     * carries a schema-valid `manifest.json`. Legacy/manifest-less views omit
     * this field and degrade to `sourcePath`. Additive + optional so Fastify's
     * response serialiser keeps it without breaking older callers.
     */
    manifest: Type.Optional(ViewManifestSchema),
}, { $id: 'ViewSummary' });
export const ViewMetadataSchema = Type.Object({
    viewId: Type.String(),
    name: Type.String(),
    description: Type.Optional(Type.String()),
    sourceSysmlPath: Type.Optional(Type.String()),
    tags: Type.Optional(Type.Array(Type.String())),
}, { $id: 'ViewMetadata' });
// ─── LayoutIntent (LV1-S1) ───────────────────────────────────────────────────
// The constrained layout-directive DSL the "layout director" LLM emits (LV1-S2)
// and the deterministic ELK + positions compiler executes (LV1-S3/S4). The LLM
// never emits coordinates or pixels — only this validated directive shape, with
// closed enums so a strict json_schema structured-output call can only produce
// a valid intent. This schema is the single source of truth; the behavioural
// surface (`defaultLayoutIntent()`, the strict json-schema export) lives in
// `response-view/layout-intent.ts` and re-exports these types.
//
// Declared ABOVE `ReactFlowLayoutJsonSchema` so the LV1-S5 persistence field
// `Type.Optional(LayoutIntentSchema)` on the layout reference resolves at
// module-eval time (TypeBox const objects evaluate top-to-bottom).
//
// CRITICAL (strict structured-output): NO field is `Type.Optional` — every
// property (including `notes` and the arrays) is required, so TypeBox auto-emits
// all keys into `required`, and `additionalProperties:false` holds at every
// object level. OpenAI/Azure strict `json_schema` mode demands exactly that
// (every property in `required`, `additionalProperties:false` everywhere).
/** Global layout flow direction → maps to `elk.direction` (LV1-S3). */
export const LayoutDirectionSchema = Type.Union([
    Type.Literal('RIGHT'),
    Type.Literal('LEFT'),
    Type.Literal('DOWN'),
    Type.Literal('UP'),
], { $id: 'LayoutDirection' });
// NB: named `LayoutIntentAlgorithm` (not `LayoutAlgorithm`) on purpose — a
// `LayoutAlgorithm` type already exists in `response-view/view-graph.ts`
// (`'elk-layered' | 'elk-force' | 'manual'`) for the Cytoscape/ELK custom
// renderer, re-exported from the package barrel. This is a DIFFERENT concept
// (the NL-directive algorithm choice) and the distinct name avoids a
// duplicate-export collision in `index.ts`.
/** Layout algorithm the intent requests → mapped to an ELK algorithm (LV1-S3). */
export const LayoutIntentAlgorithmSchema = Type.Union([
    Type.Literal('layered'),
    Type.Literal('tree'),
    Type.Literal('force'),
    Type.Literal('grid'),
    Type.Literal('radial'),
], { $id: 'LayoutIntentAlgorithm' });
/** Coarse spacing preset → mapped to ELK spacing knobs (LV1-S3). */
export const LayoutSpacingSchema = Type.Union([
    Type.Literal('compact'),
    Type.Literal('normal'),
    Type.Literal('spacious'),
], { $id: 'LayoutSpacing' });
/** The nine pin anchors (Analysis §4) → bbox-relative placement (LV1-S4). */
export const LayoutAnchorSchema = Type.Union([
    Type.Literal('top-left'),
    Type.Literal('top'),
    Type.Literal('top-right'),
    Type.Literal('left'),
    Type.Literal('center'),
    Type.Literal('right'),
    Type.Literal('bottom-left'),
    Type.Literal('bottom'),
    Type.Literal('bottom-right'),
], { $id: 'LayoutAnchor' });
/** Pin a single node to a bbox anchor. */
export const LayoutPinSchema = Type.Object({
    node: Type.String(),
    anchor: LayoutAnchorSchema,
}, { additionalProperties: false, $id: 'LayoutPin' });
/** Align a set of nodes along a shared axis (row=mean-y, column=mean-x). */
export const LayoutGroupSchema = Type.Object({
    nodes: Type.Array(Type.String()),
    align: Type.Union([Type.Literal('row'), Type.Literal('column')]),
}, { additionalProperties: false, $id: 'LayoutGroup' });
/**
 * The full layout-directive contract. Every field is required (no
 * `Type.Optional`) so the strict json-schema export lists all nine keys in
 * `required` — see the `LAYOUT_INTENT_JSON_SCHEMA` derivation in
 * `response-view/layout-intent.ts`.
 */
export const LayoutIntentSchema = Type.Object({
    /** Global flow direction. */
    direction: LayoutDirectionSchema,
    /** Requested layout algorithm. */
    algorithm: LayoutIntentAlgorithmSchema,
    /** Coarse spacing preset. */
    spacing: LayoutSpacingSchema,
    /** Per-node anchor pins. */
    pins: Type.Array(LayoutPinSchema),
    /** Node-alignment groups. */
    groups: Type.Array(LayoutGroupSchema),
    /**
     * Ids of nodes to emphasise. Emphasis is BOTH geometric (enlarge pre-ELK)
     * AND visual (accent ring in the renderer) — a single "make these stand
     * out" concept. Presentation-only; the SysML source is unchanged.
     */
    emphasis: Type.Array(Type.String()),
    /**
     * Ids of nodes to DE-emphasise: the renderer dims them (lowered opacity)
     * so the emphasised / focus elements read. Presentation-only, visual-only
     * (no ELK effect). Orthogonal to `ViewScope.hiddenNodeIds` — a de-emphasised
     * node is still drawn (faded), a hidden one is removed.
     */
    deemphasis: Type.Array(Type.String()),
    /**
     * True when the request is aesthetic/illustrative/free-form and the DSL
     * cannot express it (consumed by the LV2-S3 raster escalation).
     */
    unsupported: Type.Boolean(),
    /**
     * One-line free-text rationale. The ONLY non-deterministic field — LV1-S2
     * excludes it from any equality/cache key.
     */
    notes: Type.String(),
}, { additionalProperties: false, $id: 'LayoutIntent' });
export const ReactFlowNodeSchema = Type.Object({
    id: Type.String(),
    type: Type.Optional(Type.String()),
    data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    position: Type.Optional(Type.Object({ x: Type.Number(), y: Type.Number() })),
    /**
     * Parent node id when this node is composed inside another. Mirrors the
     * field React Flow reads to constrain a child inside its parent
     * container. Consumed by `buildReactFlowGraph()` → React Flow's
     * `parentId` + `extent: 'parent'`. Used by IBD hierarchy synthesis to
     * encode "Battery is inside Powertrain inside Vehicle".
     */
    parentNode: Type.Optional(Type.String()),
});
export const ReactFlowEdgeSchema = Type.Object({
    id: Type.String(),
    source: Type.String(),
    target: Type.String(),
    type: Type.Optional(Type.String()),
    /**
     * Per-side React Flow handle ids. Required so the IBD endpoint can pin an
     * edge to a specific port (`${portName}.s` / `${portName}.t`); without
     * this Fastify's response serialiser strips the field and the edge falls
     * back to centre-of-node anchoring.
     */
    sourceHandle: Type.Optional(Type.String()),
    targetHandle: Type.Optional(Type.String()),
    data: Type.Optional(Type.Record(Type.String(), Type.Unknown())),
    /**
     * User-positioned bend points. When present, ConnectionEdge routes the
     * polyline through these points (source → waypoint[0] → ... → target)
     * instead of using ELK / smoothstep auto-routing. Empty array is treated
     * the same as omitted. Coordinates are in the React Flow viewport frame
     * (same as `node.position`).
     */
    waypoints: Type.Optional(Type.Array(Type.Object({ x: Type.Number(), y: Type.Number() }))),
});
/**
 * Scope / visibility directive for a view — the *content* axis, orthogonal to
 * the *arrangement* axis carried by {@link LayoutIntentSchema}. Persisted as an
 * optional sibling on {@link ReactFlowLayoutJsonSchema} (NOT inside
 * `LayoutIntent`, whose eight fields are the closed spatial-director contract).
 *
 * Both fields hold stable React Flow node ids (the usage paths the auto-view
 * synthesizer mints, e.g. `PowerSubsystem` or `PowerSubsystem::dcdc`). The raw
 * `view.sysml` source is NEVER edited by scoping — a scoped view is a *faithful
 * partial* projection; the complete model stays one action away in the SysML
 * source. Consumed by the pure `applyViewScope()` filter
 * (`response-view/view-scope.ts`) at render time.
 *
 * Every field is required (no `Type.Optional`) so the strict json-schema export
 * (`VIEW_SCOPE_JSON_SCHEMA`) lists both keys in `required` — mirroring the
 * `LayoutIntentSchema` discipline.
 */
export const ViewScopeSchema = Type.Object({
    /**
     * Hard-hide these nodes (and, transitively, any descendants nested under
     * them via `parentNode`). Edges incident to a hidden node are re-anchored
     * to its nearest still-visible ancestor, or dropped when none survives.
     */
    hiddenNodeIds: Type.Array(Type.String()),
    /**
     * Collapse these container nodes to a "black box": the container node
     * itself stays, but ALL of its descendant children are hidden and their
     * internal edges dropped, while boundary flows re-anchor to the container.
     */
    collapsedContainers: Type.Array(Type.String()),
}, { additionalProperties: false, $id: 'ViewScope' });
// ─── Phase 2 — deterministic scope set/clear (PUT /views/:viewId/scope) ──────
/**
 * Request body for `PUT /views/:viewId/scope`. Carries a full `ViewScope`
 * directive to apply. A no-op scope (both arrays empty — e.g. the "Show all"
 * reveal sends `defaultViewScope()`) STRIPS any persisted scope so the on-disk
 * layout becomes byte-identical to a never-scoped view. Deterministic — NO LLM;
 * the chat NL path (`scopeView`) owns the compile.
 */
export const ViewScopePutBodySchema = Type.Object({ viewScope: ViewScopeSchema }, { additionalProperties: false, $id: 'ViewScopePutBody' });
/** Response for `PUT /views/:viewId/scope` — echoes the effective post-write scope. */
export const ViewScopePutResponseSchema = Type.Object({ written: Type.Boolean(), viewScope: ViewScopeSchema }, { $id: 'ViewScopePutResponse' });
export const ReactFlowLayoutJsonSchema = Type.Object({
    version: Type.Literal('1'),
    nodes: Type.Array(ReactFlowNodeSchema),
    edges: Type.Array(ReactFlowEdgeSchema),
    positions: Type.Optional(Type.Record(Type.String(), Type.Object({ x: Type.Number(), y: Type.Number() }))),
    /**
     * Human-readable view title shown above the canvas. Derived from the
     * synthesised SysML view package name when the layout is auto-generated
     * (`synthesizeAutoView().packageName` → drop trailing `View` suffix and
     * the timestamp). Optional for back-compat with persisted layouts that
     * pre-date this field.
     */
    title: Type.Optional(Type.String()),
    /**
     * LV1-S5 — the `LayoutIntent` that was applied to produce this layout's
     * arrangement, persisted for provenance + incremental refinement (LV1-S6
     * re-layout follow-ups read it). OPTIONAL: a no-intent / switch-OFF /
     * auto-written layout omits this field entirely, so existing on-disk
     * `layout.json` files parse unchanged and the default render path stays
     * byte-identical. The SPA does NOT re-compile this on load — it is
     * provenance only; the already-resolved `elkOptions` + `positions` below
     * are what the renderer re-applies.
     */
    layoutIntent: Type.Optional(LayoutIntentSchema),
    /**
     * LV1-S5 — the RESOLVED root ELK options (e.g. `elk.direction`,
     * `elk.algorithm`, spacing knobs) that `LayoutIntent` compiled to
     * (LV1-S3). A flat `Record<string,string>` matching the shape the
     * `buildRootOpts` compiler returns. Persisting the already-resolved opts
     * (rather than re-deriving from `layoutIntent` on load) keeps the
     * re-apply path off the LLM AND preserves any LV1-S3 nesting-unsafe
     * algorithm downgrade — the SPA re-applies these verbatim. OPTIONAL for
     * the same back-compat / byte-identical-parity reason as `layoutIntent`.
     */
    elkOptions: Type.Optional(Type.Record(Type.String(), Type.String())),
    /**
     * Scope / visibility directive — the *content* axis (which nodes render),
     * orthogonal to `layoutIntent`'s *arrangement* axis. Set by the in-place
     * scope loop (`scope-current-view.ts`) when the user asks to hide/collapse
     * parts; applied deterministically at render time by `applyViewScope()`.
     * OPTIONAL for the same back-compat / byte-identical-parity reason as
     * `layoutIntent`: a view with no scope directive omits it and renders the
     * full node set exactly as before.
     */
    viewScope: Type.Optional(ViewScopeSchema),
}, { $id: 'ReactFlowLayoutJson' });
export const ViewLayoutPutResponseSchema = Type.Object({
    written: Type.Boolean(),
    /**
     * SHA-256 hex prefix of the saved layout payload. Used as the cache key
     * for `GET /:viewId/export.svg?layoutId=`. Stable for a given layout JSON
     * so repeated saves of identical content collide harmlessly.
     */
    layoutId: Type.String({ minLength: 1 }),
}, { $id: 'ViewLayoutPutResponse' });
export const ViewExportSvgQuerySchema = Type.Object({
    layoutId: Type.Optional(Type.String()),
    /**
     * Cache-bust token (the view dir mtime) appended by the views-list scanner
     * to `thumbnailUrl`. Ignored by the handler — it only varies the URL so a
     * re-rendered view refetches instead of serving the `max-age`-cached SVG at
     * a stable URL. Declared so it's a documented, first-class query param
     * (mirrors the `?v=` cache-bust on `GET /views/:viewId/image`).
     */
    v: Type.Optional(Type.String()),
}, { $id: 'ViewExportSvgQuery' });
export const ViewIdParamSchema = Type.Object({ viewId: Type.String({ minLength: 1 }) }, { $id: 'ViewIdParam' });
export const ViewsListResponseSchema = Type.Array(ViewSummarySchema, {
    $id: 'ViewsListResponse',
});
export const ViewsDeleteAllResponseSchema = Type.Object({ deleted: Type.Integer({ minimum: 0 }) }, { $id: 'ViewsDeleteAllResponse' });
// ─── SceneDescription (IV1-S2) ─────────────────────────────────────────────
// A provider-agnostic, deterministic projection of a rendered KB view into a
// structured "scene". Produced from a `ReactFlowLayoutJson` + the response-view
// trailer by `projectSceneDescription()`
// (packages/base/src/response-view/scene-description.ts). It is the KB-grounded
// ground truth handed to every visualization engine (deterministic, LLM raster,
// diagram-as-code) so the model renders from the model's facts, never from
// chat prose.
export const ScenePortSchema = Type.Object({
    name: Type.String(),
    /** Port interface/flow type when known (e.g. 'EnergyFlow'). */
    type: Type.Optional(Type.String()),
}, { $id: 'ScenePort' });
export const ScenePortDirectionSchema = Type.Union([
    Type.Literal('in'),
    Type.Literal('out'),
    Type.Literal('inout'),
    Type.Literal('unknown'),
], { $id: 'ScenePortDirection' });
export const SceneAttributeSchema = Type.Object({ name: Type.String(), value: Type.String() }, { $id: 'SceneAttribute' });
export const SceneNodeSchema = Type.Object({
    id: Type.String(),
    /** Short display name (the usage/instance name, e.g. 'battery'). */
    name: Type.String(),
    /**
     * Part-def kind (e.g. 'Battery'); 'unknown' when the layout carried no
     * resolved kind for the node (flat fallback nodes or orphan citations).
     */
    defKind: Type.String(),
    /** Composition parent node id; absent for root nodes. */
    parentId: Type.Optional(Type.String()),
    portsIn: Type.Array(ScenePortSchema),
    portsOut: Type.Array(ScenePortSchema),
    attributes: Type.Array(SceneAttributeSchema),
    /**
     * True only for explicit unresolved-citation orphans (layout kind ===
     * 'unknown'). Flat fallback nodes (kind absent) are NOT orphans.
     */
    isOrphan: Type.Boolean(),
}, { $id: 'SceneNode' });
export const SceneEdgeSchema = Type.Object({
    id: Type.String(),
    sourceId: Type.String(),
    targetId: Type.String(),
    sourcePort: Type.Optional(Type.String()),
    targetPort: Type.Optional(Type.String()),
    /**
     * Direction of the source endpoint's port, derived from the source node's
     * port lists ('inout' collapses to 'in' — the layout does not preserve the
     * distinction). 'unknown' when the port is not found on the node.
     */
    sourcePortDirection: Type.Optional(ScenePortDirectionSchema),
    targetPortDirection: Type.Optional(ScenePortDirectionSchema),
    /** Interface/flow type of the connection (e.g. 'EnergyFlow'). */
    flowType: Type.Optional(Type.String()),
    /** Connection name when present. */
    label: Type.Optional(Type.String()),
}, { $id: 'SceneEdge' });
export const SceneOmissionSchema = Type.Object({
    /**
     * Machine-readable omission reason, e.g. 'unresolved-endpoint',
     * 'self-loop', 'flow-type-filtered'.
     */
    reason: Type.String(),
    detail: Type.Optional(Type.String()),
}, { $id: 'SceneOmission' });
export const SceneDescriptionSchema = Type.Object({
    version: Type.Literal('1'),
    title: Type.Optional(Type.String()),
    /** The render kind requested by the response-view trailer. */
    renderKind: Type.Union([
        Type.Literal('interconnection'),
        Type.Literal('tree'),
        Type.Literal('table'),
        Type.Literal('action'),
        Type.Literal('sequence'),
    ]),
    /** Flow/interface types the view was scoped to (empty = all). */
    flowTypes: Type.Array(Type.String()),
    nodes: Type.Array(SceneNodeSchema),
    edges: Type.Array(SceneEdgeSchema),
    /** Ids of nodes that have no composition parent (traversal roots). */
    rootIds: Type.Array(Type.String()),
    /**
     * Connections the renderer dropped that this layout-only projection could
     * not recover (the dropped data is destroyed inside the synthesizer).
     * Empty under `projectSceneDescription(layout, trailer)`; populated by a
     * future kbIndex-aware enrichment.
     */
    omissions: Type.Array(SceneOmissionSchema),
}, { $id: 'SceneDescription' });
// NOTE: the `LayoutIntent*` schemas + types now live ABOVE `ReactFlowNodeSchema`
// (moved there in LV1-S5) so `ReactFlowLayoutJsonSchema.layoutIntent`'s
// `Type.Optional(LayoutIntentSchema)` reference resolves at module-eval time.
//# sourceMappingURL=views.js.map